import { z } from 'zod/v4'
import { AD_UNIT_MEDIATYPES } from './adFormat'
import { CurrencyCode } from './currency'

//
// -------------- AD UNITS --------------
//

// Ad unit media types, extending the catalog's AD_UNIT_MEDIATYPES with 'Skin'.
export const KLEANADS_MEDIATYPES = [...AD_UNIT_MEDIATYPES, 'Skin'] as const
export const KleanadsMediatypeSchema = z.enum(KLEANADS_MEDIATYPES)
export type KleanadsMediatype = z.infer<typeof KleanadsMediatypeSchema>

const KleanadsSizeSchema = z.union([z.literal('fluid'), z.array(z.number())])

const KleanadsFloor = z.object({
  currency: z.string(),
  schema: z.object({
    delimiter: z.string(),
    fields: z.array(z.string()),
  }),
  values: z.any(),
  dynamic: z.boolean().nullish(),
})

const KleanadsTargeting = z.object({
  key: z.string(),
  values: z.array(z.string()),
})

const KleanadsBidderSchema = z.object({
  bidder: z.string(),
  params: z.any(),
  adUnitMediaTypes: z.array(KleanadsMediatypeSchema).nullish(),
})
export type KleanadsAdUnitBidder = z.infer<typeof KleanadsBidderSchema>

/** The bidder a One page calls prebid-server-proxy through. */
export const PUBSTACK_BIDDER = 'pubstack'

const KleanadsMediaTypeParams = z.object({
  minduration: z.number(),
  maxduration: z.number(),
  minbitrate: z.number(),
  maxbitrate: z.number(),
  placement: z.number().nullish(),
  plcmt: z.number().nullish(),
  pos: z.number(),
})
const KleanadsInstreamParams = z.object({
  mediaTypeParams: KleanadsMediaTypeParams,
  /** @deprecated legacy 0-indexed value, kept in sync for older kleanads.js readers. Use `playbackmethod`. */
  startPlayingOption: z.number().nullish(),
  /** ORTB2 `playbackmethod` (1-indexed). */
  playbackmethod: z.number().nullish(),
  skippableads: z.boolean(),
  protocols: z.array(z.string()),
})

const KleanadsAdUnitSchema = z.object({
  sizes: z.array(KleanadsSizeSchema),
  extraAdServerSizes: z.array(KleanadsSizeSchema).nullish(),
  adUnitName: z.string(),
  divId: z.string(),
  mediaTypes: z.array(KleanadsMediatypeSchema).nullish(),
  dynamic: z.boolean().nullish(),
  parallax: z.boolean().nullish(),
  adServerAdUnitName: z.string().nullish(),
  anchor: z.object({ mode: z.string() }).nullish(),
  floors: KleanadsFloor.nullish(),
  targeting: KleanadsTargeting.nullish(),
  extraTargetings: z.array(KleanadsTargeting).nullish(),
  bidders: z.array(KleanadsBidderSchema),
  headerBiddingEnabled: z.boolean(),
  canOutstream: z.boolean(),
  isInstream: z.boolean(),
  outOfPageType: z.enum(['default', 'interstitial']).nullish(),
  roadblock: z.enum(['master', 'companion']).nullish(),
  adFormat: z.string(),
  canNative: z.boolean(),
  nativeParams: z.any().nullish(),
  instreamParams: KleanadsInstreamParams.nullish(),
})

//
// -------------- Prebid Config --------------
//

const KleanadsBidderAdjustment = z.object({
  bidder: z.string(),
  adjustment: z.number(),
})

const KleanadsBidderAlias = z.object({
  bidderLabel: z.string(),
  bidderSource: z.string(),
})

const KleanadsTargetingBehaviors = z.object({
  sendKVTargetings: z.boolean(),
  biddersBlackList: z.array(z.string()),
})

const KleanadsS2SConfig = z.object({
  accountId: z.string().nullish(),
  name: z.string().nullish(),
  bidders: z.array(z.string()).nullish(),
  allowUnknownBidderCodes: z.boolean().nullish(),
  defaultVendor: z.string().nullish(),
  enabled: z.boolean().nullish(),
  timeout: z.number().nullish(),
  adapter: z.string(),
  endpoint: z.string(),
  syncEndpoint: z.string().nullish(),
  userSyncLimit: z.number().nullish(),
  syncTimeout: z.number().nullish(),
  coopSync: z.boolean().nullish(),
  defaultTtl: z.number().nullish(),
  extPrebid: z.any().nullish(),
})

const KleanadsPbjsConfigCurrencySchema = z.object({
  adServerCurrency: z.string(),
  conversionRateFile: z.string(),
  defaultRates: z.record(z.string(), z.any()),
})

const KleanadsPbjsConfigPriceGranularitySchema = z.object({
  buckets: z.array(
    z.object({
      max: z.number(),
      increment: z.number(),
      precision: z.number(),
    })
  ),
})

const KleanadsPbjsConfigSchema = z.object({
  analytics: z.any().nullish(),
  bidderAdjustments: z.array(KleanadsBidderAdjustment).nullish(),
  bidderAliases: z.array(KleanadsBidderAlias).nullish(),
  bidderConfigs: z.any().nullish(),
  currency: KleanadsPbjsConfigCurrencySchema.nullish(),
  // A string too: stacks deployed before the builder wrote an object still re-validate.
  floors: z.record(z.string(), z.unknown()).or(z.string()).nullish(),
  priceGranularity: KleanadsPbjsConfigPriceGranularitySchema.nullish(),
  realTimeData: z.any().nullish(),
  schain: z.any().nullish(),
  targetingBehaviors: KleanadsTargetingBehaviors.nullish(),
  userSync: z.any().nullish(),
  allowLocalStorage: z.boolean().nullish(),
  outbrain: z.any().nullish(),
  enableTIDs: z.boolean().nullish(),
  s2sConfig: KleanadsS2SConfig.nullish(),
})

const KleanadsPrebidConfig = z.object({
  adCallTimeout: z.number(),
  apsAccountId: z.string().nullish(),
  pbjsConfig: KleanadsPbjsConfigSchema,
})

//
// -------------- GAM Config --------------
//

const KleanadsGamConfig = z.object({
  networkId: z.number(),
  adUnitPathConfig: z.array(z.string()),
  mcmIds: z.array(z.number()).nullish(),
})

//
// -------------- Lazy Loading Config --------------
//

const KleanadsGoogleLazyLoadingRule = z.object({
  googleFetchMarginPercent: z.number().nullish(),
  googleMobileScaling: z.number().nullish(),
  googleRenderMarginPercent: z.number().nullish(),
})

const KleanadsLazyLoading = z.object({
  configName: z.string(),
  lazyLoadingGoogle: KleanadsGoogleLazyLoadingRule,
  lazyLoadingKleanads: z.object({ viewportPercent: z.number().nullish() }).nullish(),
})

//
// -------------- Refresh Config --------------
//

const KleanadsRefreshSpecificRuleSchema = z.object({
  bidders: z.array(z.string()),
  sizes: z.array(KleanadsSizeSchema),
  timer: z.number().nullish(),
})

const KleanadsAdUnitRefreshConfigRule = z.object({
  adUnitNames: z.array(z.string()),
  timer: z.number(),
  capping: z.number().nullish(),
  specificRules: z.array(KleanadsRefreshSpecificRuleSchema).nullish(),
})

const KleanadsRefreshConfigSchema = z.object({
  adUnitConfig: z.array(KleanadsAdUnitRefreshConfigRule),
})

//
// -------------- TAM Config --------------
//

const KleanadsTamConfig = z.object({
  apsPubId: z.string(),
})

//
// -------------- The whole object written to S3 --------------
//

export const KleanadsOutputSchema = z.object({
  adUnits: z.array(KleanadsAdUnitSchema),
  prebidConfig: KleanadsPrebidConfig,
  gamConfig: KleanadsGamConfig.nullish(),
  lazyLoading: KleanadsLazyLoading.nullish(),
  refreshConfig: KleanadsRefreshConfigSchema.nullish(),
  tamConfig: KleanadsTamConfig.nullish(),
})

export type KleanadsSize = z.infer<typeof KleanadsSizeSchema>
export type KleanadsAdUnit = z.infer<typeof KleanadsAdUnitSchema>
type KleanadsPbjsConfigCurrency = z.infer<typeof KleanadsPbjsConfigCurrencySchema>
type KleanadsPbjsConfigPriceGranularity = z.infer<typeof KleanadsPbjsConfigPriceGranularitySchema>
export type KleanadsPbjsConfig = z.infer<typeof KleanadsPbjsConfigSchema>
export type KleanadsRefreshSpecificRule = z.infer<typeof KleanadsRefreshSpecificRuleSchema>
export type KleanadsRefreshConfig = z.infer<typeof KleanadsRefreshConfigSchema>
export type KleanadsOutput = z.infer<typeof KleanadsOutputSchema>

export const kleanadsPbjsCurrency = (adServerCurrency: CurrencyCode): KleanadsPbjsConfigCurrency => ({
  adServerCurrency,
  conversionRateFile: 'https://cdn.jsdelivr.net/gh/prebid/currency-file@1/latest.json',
  defaultRates: {
    USD: {
      USD: 1,
      JPY: 153.95082582582583,
      BGN: 1.8353978978978978,
      CZK: 23.765015015015013,
      DKK: 7.001313813813813,
      GBP: 0.8014733483483483,
      HUF: 369.9793543543543,
      PLN: 4.029466966966966,
      RON: 4.667980480480479,
      SEK: 10.846753003003002,
      CHF: 0.9126313813813813,
      ISK: 141.42267267267266,
      NOK: 10.910285285285283,
      TRY: 32.40343468468468,
      AUD: 1.5428866366366365,
      BRL: 5.119463213213213,
      CAD: 1.3743430930930929,
      CNY: 7.238551051051051,
      HKD: 7.82835960960961,
      IDR: 16058.352102102099,
      ILS: 3.7141516516516515,
      INR: 83.42248498498498,
      KRW: 1383.061186186186,
      MXN: 16.56916291291291,
      MYR: 4.778997747747748,
      NZD: 1.6838400900900898,
      PHP: 56.82995495495495,
      SGD: 1.3605480480480479,
      THB: 36.71546546546546,
      ZAR: 18.962743993993993,
      EUR: 0.9384384384384383,
    },
  },
})

export const kleanadsPbjsPriceGranularity = (currencyCode: CurrencyCode): KleanadsPbjsConfigPriceGranularity => {
  if (currencyCode === 'NOK') {
    return {
      buckets: [
        { max: 100, increment: 0.25, precision: 2 },
        { max: 200, increment: 0.5, precision: 2 },
        { max: 400, increment: 1, precision: 2 },
        { max: 600, increment: 2, precision: 2 },
      ],
    }
  }
  return {
    buckets: [
      {
        max: 5,
        increment: 0.01,
        precision: 2,
      },
      {
        max: 8,
        increment: 0.02,
        precision: 2,
      },
      {
        max: 15,
        increment: 0.05,
        precision: 2,
      },
      {
        max: 20,
        increment: 0.1,
        precision: 2,
      },
      {
        max: 50,
        increment: 0.5,
        precision: 2,
      },
    ],
  }
}

export const BEOP_SPECIFIC_RULE: KleanadsRefreshSpecificRule = {
  bidders: ['beop'],
  sizes: [],
  timer: 60,
}
